/**
 * Shared invoice net/gross derivation.
 *
 * `finance_invoices` collection has two legacy variants:
 *   1. Locally-created invoices: `total_amount` is NET, `total_incl_vat` is GROSS.
 *   2. E-conomic-synced invoices: `total_amount` was populated from
 *      e-conomic's grossAmount and `net_amount` was stored separately —
 *      so `total_amount === total_incl_vat` and neither equals net.
 *
 * This helper hides that inconsistency so every UI location can call one
 * function and always render the correct net + gross without touching the
 * historic DB rows.
 */

export function getInvoiceAmounts(invoice) {
    if (!invoice) return { net: null, gross: null, vat: null };

    const hasVat = invoice.vat_mode === 'with_vat';
    const explicitNet = invoice.net_amount;
    const explicitGross = invoice.total_incl_vat;
    const vat = invoice.vat_amount ?? 0;
    const totalAmount = invoice.total_amount ?? 0;

    let gross;
    if (explicitGross != null && explicitGross > 0) {
        gross = explicitGross;
    } else if (hasVat) {
        gross = totalAmount + vat;
    } else {
        gross = totalAmount;
    }

    let net;
    if (explicitNet != null && explicitNet > 0) {
        net = explicitNet;
    } else if (hasVat) {
        // Prefer net = gross - vat. If vat is missing but rate is known, derive.
        if (vat > 0) {
            net = gross - vat;
        } else if (invoice.vat_rate) {
            net = gross / (1 + invoice.vat_rate / 100);
        } else {
            net = totalAmount;
        }
    } else {
        net = totalAmount;
    }

    const derivedVat = vat > 0 ? vat : Math.max(gross - net, 0);
    return {
        net: net != null ? Number(net.toFixed(2)) : null,
        gross: gross != null ? Number(gross.toFixed(2)) : null,
        vat: derivedVat != null ? Number(derivedVat.toFixed(2)) : null,
    };
}

/**
 * Convenience: just the net subtotal, or null if invoice is null.
 */
export function getInvoiceNet(invoice) {
    return getInvoiceAmounts(invoice).net;
}

/**
 * Convenience: just the gross total (incl. VAT).
 */
export function getInvoiceGross(invoice) {
    return getInvoiceAmounts(invoice).gross;
}
